import React, { useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { getCallCenterBaseUrl } from "../../service/CallCenterApi";
import ConfirmDialog from "./listing/ConfirmDialog";
import WebhookUrlPanel from "./listing/WebhookUrlPanel";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "./listing/listingStyles";

export const PAYMENT_CONFIRMATION_PROVIDERS = {
  WEBHOOK: "WEBHOOK",
  REST_POLL: "REST_POLL",
  MESSAGE_QUEUE: "MESSAGE_QUEUE",
};

const CHECKBOX_CLASS =
  "h-4 w-4 rounded border-slate-300 text-[#2B7FFF] accent-[#2B7FFF] focus:ring-[#2B7FFF]";

const FIELD_MAPPING_KEYS = ["accountNo", "amount", "reference", "paidAt", "isPaid"];

const FIELD_MAPPING_LABEL_KEYS = {
  accountNo: "ccPaymentConfirmationFieldAccountNo",
  amount: "ccPaymentConfirmationFieldAmount",
  reference: "ccPaymentConfirmationFieldReference",
  paidAt: "ccPaymentConfirmationFieldPaidAt",
  isPaid: "ccPaymentConfirmationFieldIsPaid",
};

const defaultFieldMappings = {
  accountNo: "accountNo",
  amount: "amount",
  reference: "reference",
  paidAt: "paidAt",
  isPaid: "isPaid",
};

const defaultMqConfig = {
  host: "localhost",
  port: 5672,
  username: "guest",
  password: "",
  virtualHost: "/",
  queue: "payment-confirmations",
};

const PAYLOAD_EXAMPLE = `{
  "accountNo": "ACC-12345",
  "amount": 150.00,
  "reference": "PAY-2026-001",
  "paidAt": "2026-09-15T10:30:00Z",
  "isPaid": true
}`;

export const buildPaymentConfirmationInitialValues = (data) => ({
  enabled: Boolean(data?.enabled),
  provider: data?.provider || PAYMENT_CONFIRMATION_PROVIDERS.WEBHOOK,
  webhookSecret: "",
  pollBaseUrl: data?.pollBaseUrl || "",
  pollPathTemplate: data?.pollPathTemplate || "/payments/status?accountNo={accountNo}",
  pollAuthType: data?.pollAuthType || "BEARER",
  pollAuthSecret: "",
  pollIntervalMinutes: Number(data?.pollIntervalMinutes) || 15,
  mqBrokerType: data?.mqBrokerType || "RABBITMQ",
  mqConfigJsonText: JSON.stringify(
    data?.mqConfigJson && Object.keys(data.mqConfigJson).length > 0
      ? data.mqConfigJson
      : defaultMqConfig,
    null,
    2
  ),
  fieldMappingJson: {
    ...defaultFieldMappings,
    ...(data?.fieldMappingJson || {}),
  },
});

const buildValidationSchema = (t, secretFlags) =>
  Yup.object({
    enabled: Yup.boolean(),
    provider: Yup.string().oneOf(Object.values(PAYMENT_CONFIRMATION_PROVIDERS)),
    pollIntervalMinutes: Yup.number()
      .min(1, t("ccPaymentConfirmationPollIntervalInvalid"))
      .max(1440, t("ccPaymentConfirmationPollIntervalInvalid")),
    pollBaseUrl: Yup.string().trim().when(["enabled", "provider"], {
      is: (enabled, provider) => enabled && provider === PAYMENT_CONFIRMATION_PROVIDERS.REST_POLL,
      then: (field) =>
        field
          .required(t("ccPaymentConfirmationPollBaseUrlRequired"))
          .url(t("ccPaymentConfirmationPollBaseUrlInvalid")),
    }),
    pollPathTemplate: Yup.string().trim().when(["enabled", "provider"], {
      is: (enabled, provider) => enabled && provider === PAYMENT_CONFIRMATION_PROVIDERS.REST_POLL,
      then: (field) => field.required(t("ccPaymentConfirmationPollPathRequired")),
    }),
    webhookSecret: Yup.string().trim().when(["enabled", "provider"], {
      is: (enabled, provider) =>
        enabled &&
        provider === PAYMENT_CONFIRMATION_PROVIDERS.WEBHOOK &&
        !secretFlags?.webhookSecretConfigured,
      then: (field) => field.required(t("ccPaymentConfirmationWebhookSecretRequired")),
    }),
    mqConfigJsonText: Yup.string().when(["enabled", "provider"], {
      is: (enabled, provider) => enabled && provider === PAYMENT_CONFIRMATION_PROVIDERS.MESSAGE_QUEUE,
      then: (field) => field.required(t("ccPaymentConfirmationMqConfigRequired")),
    }),
  });

const PaymentConfirmationConfigForm = ({
  t,
  initialValues,
  secretFlags,
  organizationId,
  saving,
  onSubmit,
  onTestConnection,
  testingConnection = false,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmVariant, setConfirmVariant] = useState("warning");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [pendingValues, setPendingValues] = useState(null);

  const schema = useMemo(
    () => buildValidationSchema(t, secretFlags),
    [t, secretFlags]
  );

  const webhookUrl = useMemo(() => {
    if (!organizationId) return "";
    const base = getCallCenterBaseUrl().replace(/\/$/, "");
    return `${base}/webhooks/payment-confirmation/${organizationId}`;
  }, [organizationId]);

  const submitValues = async (values) => {
    try {
      await onSubmit(values);
    } catch {
      // Parent handles toast errors.
    }
  };

  const handleFormSubmit = (values) => {
    const wasEnabled = Boolean(initialValues.enabled);
    const disabling = wasEnabled && !values.enabled;
    const providerChanged = initialValues.provider !== values.provider;

    if (disabling) {
      setConfirmVariant("warning");
      setConfirmMessage(t("ccPaymentConfirmationDisableConfirm"));
      setPendingValues(values);
      setConfirmOpen(true);
      return;
    }
    if (providerChanged && wasEnabled) {
      setConfirmVariant("warning");
      setConfirmMessage(t("ccPaymentConfirmationProviderChangeConfirm"));
      setPendingValues(values);
      setConfirmOpen(true);
      return;
    }
    submitValues(values);
  };

  const handleConfirmSave = async () => {
    if (!pendingValues) return;
    const values = pendingValues;
    setConfirmOpen(false);
    setPendingValues(null);
    await submitValues(values);
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        enableReinitialize
        onSubmit={handleFormSubmit}
      >
        {({ values, errors, touched, isSubmitting }) => (
          <Form className="space-y-6">
            <label className="flex items-center gap-3 text-sm font-medium text-slate-800">
              <Field type="checkbox" name="enabled" className={CHECKBOX_CLASS} />
              {t("ccPaymentConfirmationEnabled")}
            </label>

            <div>
              <label className={FILTER_LABEL_CLASS}>{t("ccPaymentConfirmationProvider")}</label>
              <Field as="select" name="provider" className={FILTER_FIELD_CLASS}>
                <option value={PAYMENT_CONFIRMATION_PROVIDERS.WEBHOOK}>
                  {t("ccPaymentConfirmationProviderWebhook")}
                </option>
                <option value={PAYMENT_CONFIRMATION_PROVIDERS.REST_POLL}>
                  {t("ccPaymentConfirmationProviderRestPoll")}
                </option>
                <option value={PAYMENT_CONFIRMATION_PROVIDERS.MESSAGE_QUEUE}>
                  {t("ccPaymentConfirmationProviderMessageQueue")}
                </option>
              </Field>
            </div>

            {values.provider === PAYMENT_CONFIRMATION_PROVIDERS.WEBHOOK && (
              <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div>
                  <label className={FILTER_LABEL_CLASS}>
                    {t("ccPaymentConfirmationWebhookSecret")}
                  </label>
                  <Field
                    type="password"
                    name="webhookSecret"
                    className={`${FILTER_FIELD_CLASS} ${
                      touched.webhookSecret && errors.webhookSecret ? "border-red-500" : ""
                    }`}
                    autoComplete="off"
                  />
                  {touched.webhookSecret && errors.webhookSecret ? (
                    <p className="mt-1 text-xs text-red-600">{errors.webhookSecret}</p>
                  ) : secretFlags?.webhookSecretConfigured ? (
                    <p className="mt-1 text-xs text-slate-500">{t("ccSecretConfiguredHint")}</p>
                  ) : null}
                </div>
                <WebhookUrlPanel
                  label={t("ccPaymentConfirmationWebhookUrlLabel")}
                  url={webhookUrl}
                  hint={t("ccPaymentConfirmationWebhookHint")}
                  copyLabel={t("ccPaymentConfirmationCopyUrl")}
                  copiedLabel={t("ccPaymentConfirmationUrlCopied")}
                  copyFailedLabel={t("ccPaymentConfirmationCopyUrlFailed")}
                />
              </div>
            )}

            {values.provider === PAYMENT_CONFIRMATION_PROVIDERS.REST_POLL && (
              <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div>
                  <label className={FILTER_LABEL_CLASS}>
                    {t("ccPaymentConfirmationPollBaseUrl")}
                  </label>
                  <Field
                    name="pollBaseUrl"
                    className={`${FILTER_FIELD_CLASS} ${
                      touched.pollBaseUrl && errors.pollBaseUrl ? "border-red-500" : ""
                    }`}
                    placeholder="https://api.example.com"
                  />
                  {touched.pollBaseUrl && errors.pollBaseUrl ? (
                    <p className="mt-1 text-xs text-red-600">{errors.pollBaseUrl}</p>
                  ) : null}
                </div>
                <div>
                  <label className={FILTER_LABEL_CLASS}>
                    {t("ccPaymentConfirmationPollPathTemplate")}
                  </label>
                  <Field
                    name="pollPathTemplate"
                    className={`${FILTER_FIELD_CLASS} ${
                      touched.pollPathTemplate && errors.pollPathTemplate ? "border-red-500" : ""
                    }`}
                    placeholder="/payments/status?accountNo={accountNo}"
                  />
                  {touched.pollPathTemplate && errors.pollPathTemplate ? (
                    <p className="mt-1 text-xs text-red-600">{errors.pollPathTemplate}</p>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className={FILTER_LABEL_CLASS}>
                      {t("ccPaymentConfirmationPollAuthType")}
                    </label>
                    <Field as="select" name="pollAuthType" className={FILTER_FIELD_CLASS}>
                      <option value="BEARER">Bearer</option>
                      <option value="API_KEY">API Key</option>
                    </Field>
                  </div>
                  <div>
                    <label className={FILTER_LABEL_CLASS}>
                      {t("ccPaymentConfirmationPollAuthSecret")}
                    </label>
                    <Field
                      type="password"
                      name="pollAuthSecret"
                      className={FILTER_FIELD_CLASS}
                      autoComplete="off"
                    />
                    {secretFlags?.pollAuthSecretConfigured ? (
                      <p className="mt-1 text-xs text-slate-500">{t("ccSecretConfiguredHint")}</p>
                    ) : null}
                  </div>
                </div>
                <div>
                  <label className={FILTER_LABEL_CLASS}>
                    {t("ccPaymentConfirmationPollInterval")}
                  </label>
                  <Field
                    type="number"
                    name="pollIntervalMinutes"
                    className={`${FILTER_FIELD_CLASS} ${
                      touched.pollIntervalMinutes && errors.pollIntervalMinutes
                        ? "border-red-500"
                        : ""
                    }`}
                    min="1"
                    max="1440"
                  />
                  {touched.pollIntervalMinutes && errors.pollIntervalMinutes ? (
                    <p className="mt-1 text-xs text-red-600">{errors.pollIntervalMinutes}</p>
                  ) : null}
                </div>
              </div>
            )}

            {values.provider === PAYMENT_CONFIRMATION_PROVIDERS.MESSAGE_QUEUE && (
              <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div>
                  <label className={FILTER_LABEL_CLASS}>
                    {t("ccPaymentConfirmationMqBrokerType")}
                  </label>
                  <Field as="select" name="mqBrokerType" className={FILTER_FIELD_CLASS}>
                    <option value="RABBITMQ">RabbitMQ</option>
                  </Field>
                </div>
                <div>
                  <label className={FILTER_LABEL_CLASS}>
                    {t("ccPaymentConfirmationMqConfigJson")}
                  </label>
                  <Field
                    as="textarea"
                    name="mqConfigJsonText"
                    rows={8}
                    className={`w-full rounded-xl border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-700 outline-none focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15 ${
                      touched.mqConfigJsonText && errors.mqConfigJsonText ? "border-red-500" : ""
                    }`}
                  />
                  {touched.mqConfigJsonText && errors.mqConfigJsonText ? (
                    <p className="mt-1 text-xs text-red-600">{errors.mqConfigJsonText}</p>
                  ) : null}
                  {secretFlags?.mqPasswordConfigured ? (
                    <p className="mt-1 text-xs text-slate-500">{t("ccSecretConfiguredHint")}</p>
                  ) : null}
                  <p className="mt-1 text-xs text-slate-500">{t("ccPaymentConfirmationMqConfigHint")}</p>
                </div>
              </div>
            )}

            <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">
                  {t("ccPaymentConfirmationFieldMapping")}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  {t("ccPaymentConfirmationFieldMappingHint")}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {FIELD_MAPPING_KEYS.map((key) => (
                  <div key={key}>
                    <label className={FILTER_LABEL_CLASS}>
                      {t(FIELD_MAPPING_LABEL_KEYS[key])}
                    </label>
                    <Field name={`fieldMappingJson.${key}`} className={FILTER_FIELD_CLASS} />
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <h3 className="text-sm font-semibold text-slate-800">
                {t("ccPaymentConfirmationPayloadExampleTitle")}
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                {t("ccPaymentConfirmationPayloadExampleHint")}
              </p>
              <pre className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700">
                {PAYLOAD_EXAMPLE}
              </pre>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={isSubmitting || saving}
                className={`${LISTING_PRIMARY_BUTTON_CLASS} disabled:opacity-50`}
              >
                {saving || isSubmitting ? t("ccSaving") : t("ccPaymentConfirmationSave")}
              </button>
              {onTestConnection ? (
                <button
                  type="button"
                  disabled={testingConnection}
                  onClick={onTestConnection}
                  className="inline-flex h-10 items-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                >
                  {testingConnection ? t("ccProcessing") : t("ccPaymentConfirmationTestConnection")}
                </button>
              ) : null}
            </div>
          </Form>
        )}
      </Formik>

      <ConfirmDialog
        open={confirmOpen}
        title={t("ccConfirmDialogTitle")}
        message={confirmMessage}
        cancelLabel={t("ccCancel")}
        confirmLabel={t("ccConfirmDialogProceed")}
        processingLabel={t("ccProcessing")}
        variant={confirmVariant}
        loading={saving}
        onConfirm={handleConfirmSave}
        onClose={() => {
          setConfirmOpen(false);
          setPendingValues(null);
        }}
      />
    </>
  );
};

export const buildPaymentConfirmationPayload = (values, t) => {
  const payload = {
    enabled: Boolean(values.enabled),
    provider: values.provider,
    pollIntervalMinutes: Number(values.pollIntervalMinutes) || 15,
    fieldMappingJson: values.fieldMappingJson,
  };

  if (values.webhookSecret?.trim()) {
    payload.webhookSecret = values.webhookSecret.trim();
  }
  if (values.provider === PAYMENT_CONFIRMATION_PROVIDERS.REST_POLL) {
    payload.pollBaseUrl = values.pollBaseUrl?.trim() || "";
    payload.pollPathTemplate = values.pollPathTemplate?.trim() || "";
    payload.pollAuthType = values.pollAuthType?.trim() || "BEARER";
    if (values.pollAuthSecret?.trim()) {
      payload.pollAuthSecret = values.pollAuthSecret.trim();
    }
  }
  if (values.provider === PAYMENT_CONFIRMATION_PROVIDERS.MESSAGE_QUEUE) {
    payload.mqBrokerType = values.mqBrokerType?.trim() || "RABBITMQ";
    try {
      payload.mqConfigJson = JSON.parse(values.mqConfigJsonText || "{}");
    } catch {
      throw new Error(
        t ? t("ccPaymentConfirmationInvalidMqJson") : "Invalid MQ connection JSON"
      );
    }
  }
  return payload;
};

export default PaymentConfirmationConfigForm;
